import React, { useState } from "react";
import { toast } from '../../../../../../ui/toast';
import {
  deleteTranslation,
  getJobPost,
  saveTranslation,
  suggestTranslation,
} from "../../../../../../utils/api_service/JobPosting";

const SUPPORTED_LANGUAGES = [
  { value: "id", label: "Indonesian (Bahasa)" },
  { value: "ms", label: "Malay (Bahasa Melayu)" },
  { value: "zh", label: "Chinese (Simplified)" },
  { value: "zh-TW", label: "Chinese (Traditional)" },
  { value: "ja", label: "Japanese" },
  { value: "ko", label: "Korean" },
  { value: "th", label: "Thai" },
  { value: "vi", label: "Vietnamese" },
  { value: "ar", label: "Arabic" },
  { value: "fr", label: "French" },
  { value: "de", label: "German" },
  { value: "es", label: "Spanish" },
  { value: "pt", label: "Portuguese" },
  { value: "si", label: "Sinhala" },
  { value: "ta", label: "Tamil" },
  { value: "ru", label: "Russian" },
];

type TranslationPanelProps = {
  jobPostingId: string;
  availableLanguages: string[];
  currentJsonTemplate: string;
  currentHtmlTemplate: string;
  onLoadTranslation: (
    jsonTemplate: string,
    htmlTemplate: string,
    lang: string,
    autoSave?: boolean,
  ) => void;
  onTranslationsChanged: (langs: string[]) => void;
  onBeforeSuggest?: () => Promise<boolean>;
  onGetCurrentContent?: () => { html: string; json: string };
};

const TranslationPanel: React.FC<TranslationPanelProps> = ({
  jobPostingId,
  availableLanguages,
  currentJsonTemplate,
  currentHtmlTemplate,
  onLoadTranslation,
  onTranslationsChanged,
  onBeforeSuggest,
  onGetCurrentContent,
}) => {
  const [selectedLang, setSelectedLang] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeLang, setActiveLang] = useState<string | null>(null);
  const [deletingLang, setDeletingLang] = useState<string | null>(null);
  const [loadingLang, setLoadingLang] = useState<string | null>(null);

  const langLabel = (code: string) =>
    SUPPORTED_LANGUAGES.find((l) => l.value === code)?.label ?? code;

  const saveCurrentState = async (): Promise<boolean> => {
    if (!activeLang) {
      if (onBeforeSuggest) {
        return await onBeforeSuggest();
      }
      return true;
    } else {
      if (onGetCurrentContent) {
        try {
          const content = onGetCurrentContent();
          const res = await saveTranslation(jobPostingId, activeLang, {
            jsonTemplate: content.json,
            htmlTemplate: content.html,
          });
          return res.ok;
        } catch {
          return false;
        }
      }
      return true;
    }
  };

  const handleLoadExisting = async (lang: string) => {
    if (activeLang === lang) return;
    setLoadingLang(lang);
    try {
      await saveCurrentState();
      const res = await getJobPost(jobPostingId, lang);
      if (!res.ok) throw new Error();
      const data = await res.json();
      const jobPost = data.jobPosting || data;
      onLoadTranslation(
        jobPost.jsonTemplate || "",
        jobPost.htmlTemplate || "",
        lang,
      );
      setActiveLang(lang);
    } catch {
      toast.error(`Failed to load translation for ${langLabel(lang)}`, {
        position: "bottom-center",
      });
    } finally {
      setLoadingLang(null);
    }
  };

  const handleLoadOriginal = async () => {
    if (activeLang === null) return;
    setLoadingLang("original");
    try {
      await saveCurrentState();
      // Always explicitly request ?lang=en so the backend returns the English
      // content even when the main record's template was saved in another language.
      const res = await getJobPost(jobPostingId, "en");
      if (!res.ok) throw new Error();
      const data = await res.json();
      const jobPost = data.jobPosting || data;
      onLoadTranslation(
        jobPost.jsonTemplate || "",
        jobPost.htmlTemplate || "",
        "",
      );
      setActiveLang(null);
    } catch {
      toast.error("Failed to load original template", {
        position: "bottom-center",
      });
    } finally {
      setLoadingLang(null);
    }
  };

  const handleSuggest = async () => {
    if (!selectedLang) {
      toast.error("Select a target language first", {
        position: "bottom-center",
      });
      return;
    }
    if (!jobPostingId) {
      toast.error("Save the job posting settings first", {
        position: "bottom-center",
      });
      return;
    }
    setSuggesting(true);
    try {
      if (onBeforeSuggest) {
        const saved = await onBeforeSuggest();
        if (!saved) {
          toast.error(
            "Failed to save current template before generating suggestion",
            { position: "bottom-center" },
          );
          setSuggesting(false);
          return;
        }
      }
      const res = await suggestTranslation(jobPostingId, selectedLang);
      if (!res.ok) throw new Error();
      const data = await res.json();
      onLoadTranslation(
        data.jsonTemplate,
        data.htmlTemplate,
        selectedLang,
        true,
      );
      setActiveLang(selectedLang);
      setSelectedLang("");
      toast.success(
        `Translation suggested for ${langLabel(selectedLang)}. Review and save.`,
        {
          position: "bottom-center",
        },
      );
    } catch {
      toast.error("Failed to generate translation suggestion", {
        position: "bottom-center",
      });
    } finally {
      setSuggesting(false);
    }
  };

  const handleSave = async () => {
    if (!activeLang) {
      toast.error("Load a translation first", { position: "bottom-center" });
      return;
    }
    setSaving(true);
    try {
      let jsonTemplate = currentJsonTemplate;
      let htmlTemplate = currentHtmlTemplate;
      if (onGetCurrentContent) {
        const content = onGetCurrentContent();
        jsonTemplate = content.json;
        htmlTemplate = content.html;
      }
      const res = await saveTranslation(jobPostingId, activeLang, {
        jsonTemplate,
        htmlTemplate,
      });
      if (!res.ok) throw new Error();
      const updated = availableLanguages.includes(activeLang)
        ? availableLanguages
        : [...availableLanguages, activeLang];
      onTranslationsChanged(updated);
      setSelectedLang("");
      toast.success(`Translation saved for ${langLabel(activeLang)}`, {
        position: "bottom-center",
      });
    } catch {
      toast.error("Failed to save translation", { position: "bottom-center" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (lang: string) => {
    setDeletingLang(lang);
    try {
      const res = await deleteTranslation(jobPostingId, lang);
      if (!res.ok) throw new Error();
      const updated = availableLanguages.filter((l) => l !== lang);
      onTranslationsChanged(updated);
      if (activeLang === lang) setActiveLang(null);
      toast.success(`Translation removed for ${langLabel(lang)}`, {
        position: "bottom-center",
      });
    } catch {
      toast.error("Failed to delete translation", {
        position: "bottom-center",
      });
    } finally {
      setDeletingLang(null);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <svg
          className="h-4 w-4 text-emerald-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
          />
        </svg>
        <h3 className="text-sm font-extrabold text-slate-900">Translations</h3>
      </div>

      {/* Existing translations */}
      {(availableLanguages.length > 0 || activeLang) && (
        <div className="mb-3 flex flex-wrap gap-2">
          <button
            onClick={handleLoadOriginal}
            disabled={loadingLang !== null || suggesting || saving}
            className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors disabled:opacity-50 ${
              activeLang === null
                ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                : "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
            }`}
          >
            {loadingLang === "original" ? (
              <svg
                className="h-3 w-3 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            ) : null}
            <span>Original (English)</span>
          </button>

          {availableLanguages.map((lang) => (
            <div
              key={lang}
              onClick={() => handleLoadExisting(lang)}
              role="button"
              tabIndex={0}
              className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold cursor-pointer transition-colors ${
                activeLang === lang
                  ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                  : "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
              }`}
            >
              {loadingLang === lang ? (
                <svg
                  className="h-3 w-3 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              ) : null}
              <span>{langLabel(lang)}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(lang);
                }}
                disabled={deletingLang === lang}
                className="ml-0.5 rounded-full p-0.5 hover:bg-red-100 hover:text-red-600 transition-colors"
                title="Remove translation"
              >
                {deletingLang === lang ? (
                  <svg
                    className="h-3 w-3 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                ) : (
                  <svg
                    className="h-3 w-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Suggest new translation */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <select
          value={selectedLang}
          onChange={(e) => setSelectedLang(e.target.value)}
          className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
        >
          <option value="">Select language…</option>
          {SUPPORTED_LANGUAGES.filter(
            (l) => !availableLanguages.includes(l.value),
          ).map((l) => (
            <option key={l.value} value={l.value}>
              {l.label}
            </option>
          ))}
        </select>
        <button
          onClick={handleSuggest}
          disabled={suggesting || !selectedLang || !jobPostingId}
          className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {suggesting ? (
            <>
              <svg
                className="h-4 w-4 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              Translating…
            </>
          ) : (
            "AI Translate"
          )}
        </button>
        {activeLang && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-2 text-sm font-semibold text-emerald-700 shadow-sm transition hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <svg
                  className="h-4 w-4 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Saving…
              </>
            ) : (
              `Save ${langLabel(activeLang)}`
            )}
          </button>
        )}
      </div>
      {!jobPostingId && (
        <p className="mt-2 text-xs text-amber-600">
          Complete creating the job post and save it (including a valid URL).
          Once the job post is saved, you can add or edit language translations
          for this job post.{" "}
        </p>
      )}
    </div>
  );
};

export default TranslationPanel;
